import { ReactNode, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, Shield, CheckCircle2 } from "lucide-react";
import { useFeatureAccess } from "@/utils/featureAccess";
import { useProfile } from "@/hooks/useProfile";

interface SubscriptionGateProps {
  children: ReactNode;
}

const ACTIVATION_POLL_MS = 2000;
const ACTIVATION_MAX_ATTEMPTS = 8; // ~16s max wait

export function SubscriptionGate({ children }: SubscriptionGateProps) {
  const { isPro, isLoading: accessLoading } = useFeatureAccess();
  const { refetchProfile } = useProfile();
  const [searchParams] = useSearchParams();
  const [activating, setActivating] = useState(false);

  const shouldPollForActivation = useMemo(
    () => searchParams.get("success") === "true",
    [searchParams]
  );

  useEffect(() => {
    if (!shouldPollForActivation || isPro) return;

    let attempts = 0;
    let stopped = false;

    const poll = async () => {
      attempts += 1;
      const result = await refetchProfile();
      const nextPlan = (result.data as any)?.plan_key;
      if (nextPlan === "pro") {
        stopped = true;
        setActivating(false);
        return;
      }
      if (attempts >= ACTIVATION_MAX_ATTEMPTS) {
        stopped = true;
        setActivating(false);
      }
    };

    setActivating(true);
    const interval = setInterval(() => {
      if (stopped) return;
      void poll();
    }, ACTIVATION_POLL_MS);

    // immediate first poll
    void poll();

    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [shouldPollForActivation, isPro, refetchProfile]);

  if (accessLoading || activating) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-zinc-600 dark:text-zinc-300">
          <Loader2 className="h-6 w-6 animate-spin" />
          <p>{activating ? "Activating your subscription..." : "Loading your account..."}</p>
        </div>
      </div>
    );
  }

  if (isPro) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-50 via-white to-blue-50 dark:from-zinc-900 dark:via-zinc-900 dark:to-blue-950">
      <Card className="max-w-2xl w-full shadow-xl border border-zinc-200 dark:border-zinc-800">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
            <Sparkles className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-3xl font-bold text-zinc-900 dark:text-white">
            Unlock LaunchGuide Pro
          </CardTitle>
          <p className="text-zinc-600 dark:text-zinc-400">
            Subscribe for €29/month to access playbooks, analysis, agents, and every AI tool.
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              "All playbooks and task flows",
              "Website analysis and research tools",
              "AI agents and competitor insights",
              "Ongoing updates and new tools",
            ].map((item) => (
              <div
                key={item}
                className="flex items-center gap-2 rounded-lg border border-zinc-200 dark:border-zinc-800 px-3 py-2 bg-white dark:bg-zinc-900/60"
              >
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                <span className="text-sm text-zinc-700 dark:text-zinc-300">{item}</span>
              </div>
            ))}
          </div>

          <div className="text-center space-y-2">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200">
              <Shield className="h-4 w-4" />
              <span className="text-sm font-medium">Secure checkout powered by Stripe</span>
            </div>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              Cancel anytime. No hidden fees.
            </p>
          </div>

          <Button
            size="lg"
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground text-lg py-6"
            onClick={() => {
              window.location.href = "/pricing";
            }}
          >
            Subscribe for €29/month
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
